import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

// === Upsell: add-to-cart + reload + reopen drawer (robuste) ===
document.addEventListener("click", async (e) => {
  const btn = e.target.closest("[data-add-variant]");
  if (!btn) return;

  e.preventDefault();
  e.stopPropagation();

  const id = Number(btn.getAttribute("data-add-variant"));
  if (!id) return;

  const original = btn.textContent;
  btn.disabled = true;
  btn.textContent = "Ajout...";

  try {
    const addRes = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id, quantity: 1 })
    });
    if (!addRes.ok) throw new Error("add failed");

    sessionStorage.setItem("reopenCartDrawer", "1");
    window.location.reload();
  } catch (err) {
    console.error("Upsell add error:", err);
    btn.disabled = false;
    btn.textContent = original + " (erreur)";
  }
});

// === Réouverture auto après reload ===
(function reopenDrawerAfterReload(){
  if (sessionStorage.getItem("reopenCartDrawer") !== "1") return;

  const MAX_TRIES = 12;
  let tries = 0;

  function tryOpen(){
    tries++;
    const trigger =
      document.querySelector('[on\\:click="/open"]') ||
      document.querySelector('.header-actions__action[on\\:click="/open"]') ||
      document.querySelector('#shopify-section-cart-drawer .header-actions__action') ||
      document.querySelector('button[aria-label*="open_cart_drawer"]');

    if (trigger) {
      try { trigger.click(); } catch(e){}
      sessionStorage.removeItem("reopenCartDrawer");
      return;
    }

    const dialog =
      document.querySelector("#shopify-section-cart-drawer dialog.cart-drawer__dialog") ||
      document.querySelector("cart-drawer-component dialog.cart-drawer__dialog");
    if (dialog && typeof dialog.showModal === "function") {
      try { dialog.showModal(); } catch(e){}
      sessionStorage.removeItem("reopenCartDrawer");
      return;
    }

    const comp = document.querySelector("cart-drawer-component");
    if (comp && typeof comp.open === "function") {
      try { comp.open(); } catch(e){}
      sessionStorage.removeItem("reopenCartDrawer");
      return;
    }

    if (tries < MAX_TRIES) {
      setTimeout(tryOpen, 200);
    } else {
      sessionStorage.removeItem("reopenCartDrawer");
    }
  }

  window.addEventListener("DOMContentLoaded", () => setTimeout(tryOpen, 200));
})();

/* =========================================================================
   ✅ NOUVELLE APPROCHE : attendre le vrai changement du panier, puis re-render
   - On écoute remove / + / − / change quantité
   - On lit /cart.js (snapshot avant)
   - On poll /cart.js jusqu’à ce que item_count OU total_price change
   - On re-render cart-drawer + header-group, puis on rouvre si besoin
   ========================================================================= */
(function cartDrawerWaitForChangeThenRefresh(){
  let pending = false;

  async function readCart() {
    const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!r.ok) throw new Error('cart.js failed');
    return r.json();
  }

  async function rerenderSections(reopen) {
    const res = await fetch(`${window.location.pathname}?sections=cart-drawer,header-group`, { method: 'GET' });
    if (!res.ok) throw new Error('sections failed');
    const json = await res.json();

    const parser = new DOMParser();

    // Remplacer le drawer
    if (json['cart-drawer']) {
      const doc = parser.parseFromString(json['cart-drawer'], 'text/html');
      const fresh = doc.querySelector('#shopify-section-cart-drawer');
      const current = document.querySelector('#shopify-section-cart-drawer');
      if (fresh && current) current.replaceWith(fresh);
    }

    // Remplacer le header-group (bulle compteur)
    if (json['header-group']) {
      const doc = parser.parseFromString(json['header-group'], 'text/html');
      const fresh = doc.querySelector('[id^="shopify-section-"][id$="__header-group"]');
      const current = document.querySelector('[id^="shopify-section-"][id$="__header-group"]');
      if (fresh && current) current.replaceWith(fresh);
    }

    // Ré-ouvrir si besoin
    if (reopen) {
      const trigger =
        document.querySelector('[on\\:click="/open"]') ||
        document.querySelector('.header-actions__action[on\\:click="/open"]') ||
        document.querySelector('#shopify-section-cart-drawer .header-actions__action') ||
        document.querySelector('button[aria-label*="open_cart_drawer"]');

      if (trigger) {
        try { trigger.click(); } catch(e) {}
      } else {
        const dialog = document.querySelector('#shopify-section-cart-drawer dialog.cart-drawer__dialog');
        if (dialog && typeof dialog.showModal === 'function') {
          try { dialog.showModal(); } catch(e) {}
        }
      }
    }
  }

  async function waitThenRefresh() {
    if (pending) return;
    pending = true;

    const wasOpen = !!document.querySelector('#shopify-section-cart-drawer dialog.cart-drawer__dialog[open]');

    // Snapshot avant
    let before;
    try { before = await readCart(); } catch { before = null; }

    // Poll jusqu’à détection de changement
    const MAX_TRIES = 20; // ~4s max
    const DELAY = 200;
    let changed = false;

    for (let i = 0; i < MAX_TRIES; i++) {
      await new Promise(r => setTimeout(r, DELAY));
      try {
        const after = await readCart();
        if (!before || after.item_count !== before.item_count || after.total_price !== before.total_price) {
          changed = true;
          break;
        }
      } catch { /* ignore */ }
    }

    // Même si pas détecté (rare), on force un re-render une fois
    try {
      await rerenderSections(wasOpen);
    } catch (e) {
      console.error('rerender sections error', e);
    } finally {
      pending = false;
    }
  }

  // Déclencheurs : remove / + / − / liens / inputs quantité / submits de change
  function shouldTrigger(e) {
    return !!(
      e.target.closest('[data-cart-remove]') ||
      e.target.closest('.cart-remove') ||
      e.target.closest('button[name="remove"]') ||
      e.target.closest('a[href*="/cart/change"]') ||
      e.target.closest('[on\\:click*="remove"]') ||
      e.target.closest('[data-quantity-action]') ||
      e.target.closest('form[action*="/cart/change"]')
    );
  }

  document.addEventListener('click', (e) => {
    if (shouldTrigger(e)) {
      // On laisse l’action native partir, puis on attend le vrai changement
      waitThenRefresh();
    }
  });

  document.addEventListener('change', (e) => {
    if (!e.target.closest('#shopify-section-cart-drawer')) return;
    if (
      e.target.matches('input[name^="updates"]') ||
      e.target.matches('input[type="number"][name*="quantity"]') ||
      e.target.matches('select[name^="updates"]')
    ) {
      waitThenRefresh();
    }
  });

  document.addEventListener('submit', (e) => {
    if (e.target.closest('#shopify-section-cart-drawer') && /\/cart\/change/i.test(e.target.action || '')) {
      waitThenRefresh();
    }
  });
})();
